import { Box } from "@prismicio/editor-ui"
import { type PropsWithChildren, type ReactNode, Suspense } from "react"

import { routeAnimationDuration } from "../app/routeAnimation"
import { useRole } from "../app/services/user"
import {
  headerHeight,
  mainContentMaxWidth,
  sidePanelPadding as getSidePanelPadding,
  sidePanelWidth
} from "../document/documentLayout"
import { useDocumentListLocales } from "./useDocumentListLocales"

const contentMaxHeight = `calc(100vh - ${headerHeight}px)`

interface DocumentListLayoutProps {
  header: ReactNode
  body: ReactNode
  sidePanel: ReactNode
}

export function DocumentListLayout(props: DocumentListLayoutProps) {
  const { header, body, sidePanel } = props

  return (
    <Box
      display="grid"
      width="100%"
      animation={{ name: "fadeIn", duration: routeAnimationDuration }}
      height={contentMaxHeight}
    >
      <Suspense>
        <SuperUserPanel />
      </Suspense>
      <Box justifyContent="center">
        <Box
          position="relative"
          top={headerHeight}
          maxWidth={mainContentMaxWidth}
          width="100%"
        >
          <SidePanel>{sidePanel}</SidePanel>
          {body}
        </Box>
      </Box>
      {header}
    </Box>
  )
}

function SuperUserPanel() {
  const { locale } = useDocumentListLocales()

  const { isSuperUser } = useRole(locale)

  const warningColor = isSuperUser && import.meta.env.PROD

  return (
    <Box
      position="absolute"
      backgroundColor={warningColor ? "tomato10" : "inherit"}
      width="100%"
      height="100%"
    />
  )
}

export function SidePanel(props: PropsWithChildren) {
  const { children } = props

  return (
    <Box
      as="nav"
      position="sticky"
      top={headerHeight}
      gap={16}
      width={sidePanelWidth}
      flexShrink={0}
      maxHeight={contentMaxHeight}
      flexDirection="column"
    >
      {children}
    </Box>
  )
}

// Assign padding to accommodate internal content scrollbar
export const sidePanelPadding = getSidePanelPadding("left")
